/*
 * Header cart icon + add-to-cart -> open the cart drawer (sidebar).
 *
 * The drawer's open() is globally suppressed by sm-card-product.js to power the
 * old toast behaviour; that file preserves the real method as
 * CartDrawer.prototype.__smRealOpen. This trigger uses that preserved method for
 * deliberate opens, so Dawn's own auto-open stays suppressed and we control when
 * the drawer appears.
 *
 * Opens the drawer on:
 *   1. a click of any header cart control, and
 *   2. any successful POST to /cart/add (product cards, PDP, personalised popup)
 *      — detected by wrapping fetch + XHR, so every add path is covered.
 *
 * Refresh-on-open: before opening, fetch fresh cart-drawer markup via the Section
 * Rendering API and swap #CartDrawer, so the drawer always reflects the current
 * cart (including the item just added, whatever flow added it).
 *
 * Progressive enhancement: if the drawer or __smRealOpen is unavailable, a header
 * click does NOT preventDefault, so the native <a href="/cart"> still navigates.
 */
(function () {
  // Header cart controls only: the ai-gen header icons and Dawn's (hidden)
  // anchor. Excludes other /cart links (e.g. the drawer's own "view cart").
  var SELECTOR = 'a[href$="/cart"][class*="ai-header-icon"], #cart-icon-bubbles';

  function getDrawer() {
    return document.querySelector('cart-drawer');
  }

  // Open the drawer NOW. Prefer the preserved Dawn open() (focus trap, a11y);
  // if it isn't ready yet (sm-card-product.js hasn't run), open directly so the
  // cart icon NEVER falls through to /cart when a drawer exists.
  function tryOpen(drawer, trigger) {
    // Flag the slide so CSS can quiet the offers marquee for its duration —
    // two concurrent animations stutter on phones. Cleared just after the
    // 0.32s transition ends.
    drawer.classList.add('sm-opening');
    clearTimeout(drawer.__smOpenTimer);
    drawer.__smOpenTimer = setTimeout(function () {
      drawer.classList.remove('sm-opening');
    }, 420);

    if (typeof drawer.__smRealOpen === 'function') {
      drawer.__smRealOpen(trigger);
      return;
    }
    drawer.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    var overlay = drawer.querySelector('#CartDrawer-Overlay');
    if (overlay && !overlay.__smBound) {
      overlay.__smBound = true;
      overlay.addEventListener('click', function () {
        drawer.classList.remove('active');
        document.body.classList.remove('overflow-hidden');
      });
    }
  }

  function isAddUrl(url) {
    return /\/cart\/add(\.js)?(\?|$)/.test(String(url || ''));
  }

  // Fetch fresh drawer markup and swap it in, so the drawer shows the live cart.
  function refreshDrawer(drawer) {
    var url = window.location.pathname + '?section_id=cart-drawer';
    return fetch(url, { cache: 'no-store' })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (!html) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('#CartDrawer');
        var cur = drawer.querySelector('#CartDrawer');
        if (!fresh || !cur) return;
        cur.innerHTML = fresh.innerHTML;
        var overlay = drawer.querySelector('#CartDrawer-Overlay');
        if (overlay && typeof drawer.close === 'function') {
          overlay.addEventListener('click', drawer.close.bind(drawer));
        }
        var freshRoot = parsed.querySelector('cart-drawer');
        drawer.classList.toggle(
          'is-empty',
          !!(freshRoot && freshRoot.classList.contains('is-empty'))
        );
      })
      .catch(function () { /* open with existing contents */ });
  }

  /* ------------------------------------------------------------------
   * "You may also like" loading.
   *
   * We fetch the section ourselves rather than driving Dawn's
   * <product-recommendations> element. That element lazy-loads behind an
   * IntersectionObserver that doesn't fire for an off-screen drawer, and
   * kicking it by hand proved unreliable on real phones: it could leave
   * non-card markup behind, which then looked "loaded" forever.
   *
   * "Loaded" therefore means AN ACTUAL CARD IS PRESENT (.rec), never merely
   * "innerHTML isn't empty" — that distinction is the bug that made the
   * section stay stubbornly missing.
   * ------------------------------------------------------------------ */
  var recsCache = { productId: null, html: '' };
  // The live <ul.rec-track> element. Cart re-renders are handled by MOVING this
  // same node into the fresh section rather than re-parsing HTML: re-creating
  // the <img> elements made every thumbnail repaint, which read as flicker.
  var recsNode = null;

  // The drawer content can exist more than once in the document (a section
  // re-render can nest a second <cart-drawer>, leaving a stale hidden copy).
  // A plain querySelector then returns the HIDDEN one — which is why the list
  // could load with 6 cards and still measure zero height while the visible
  // copy stayed empty. Always work on the copy that actually generates boxes.
  function recsEl(drawer) {
    var all = document.querySelectorAll('.sm-drawer-recs product-recommendations');
    for (var i = 0; i < all.length; i++) {
      if (all[i].getClientRects().length || all[i].offsetParent) return all[i];
    }
    // none rendered yet (drawer still closed) — fall back to the drawer's own
    return (drawer && drawer.querySelector('.sm-drawer-recs product-recommendations')) || all[0] || null;
  }

  function recsAreLoaded(el) {
    return !!el.querySelector('.rec');
  }

  /* On-device measurement showed the section, its head, the track and the
   * cards ALL reporting height 0 — while the section is a static block with
   * 36px of padding, height:auto, visibility:visible, no display:none
   * anywhere, and 7440 chars of content inside. A rendered block cannot
   * measure 0: the browser had the DOM but never generated boxes for the
   * subtree. (It renders fine after a cart re-render, and on desktop.)
   *
   * Toggling display forces the box tree to be rebuilt for that subtree. */
  function forceRelayout(el) {
    var sec = el && el.closest ? el.closest('.sm-drawer-recs') : null;
    if (!sec) return;
    // SYNCHRONOUS, in the same task as the content write. An earlier version
    // did this inside requestAnimationFrame — i.e. after a paint — so the
    // hidden state was shown for a frame and the section visibly flickered.
    // Run inline and the browser never paints the intermediate state.
    if (sec.offsetHeight > 0) return; // already laid out — do nothing
    // One-shot. Repeating it every health tick made the thumbnails strobe.
    if (sec.__smRelaidOut) return;
    sec.__smRelaidOut = true;
    var prev = sec.style.display;
    sec.style.display = 'none';
    void sec.offsetHeight; // flush the "none"
    sec.style.display = prev || '';
    void sec.offsetHeight; // flush the restore, still before any paint
  }

  function fetchDrawerRecs(drawer, el) {
    if (el.__smFetching) return;
    // The health interval retries every 400ms; don't let a failing request
    // turn that into a request storm.
    var now = new Date().getTime();
    if (el.__smFetchedAt && now - el.__smFetchedAt < 1500) return;
    var productId = el.dataset.productId;
    var base = el.dataset.url;
    if (!base || !productId) return;
    el.__smFetching = true;
    el.__smFetchedAt = now;

    var url =
      base +
      (base.indexOf('?') === -1 ? '?' : '&') +
      'product_id=' +
      encodeURIComponent(productId) +
      '&section_id=' +
      encodeURIComponent(el.dataset.sectionId || 'sm-cart-drawer-recs');

    fetch(url, { cache: 'no-store' })
      .then(function (r) {
        return r.ok ? r.text() : null;
      })
      .then(function (html) {
        el.__smFetching = false;
        if (!html) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('product-recommendations');
        // Only accept a payload that actually contains cards.
        if (!fresh || !fresh.querySelector('.rec')) return;
        var live = recsEl(drawer);
        if (!live) return;
        var ul = fresh.querySelector('.rec-track');
        if (!ul) return;
        live.innerHTML = '';
        live.appendChild(document.importNode(ul, true));
        recsNode = live.querySelector('.rec-track');
        recsCache = { productId: productId, html: fresh.innerHTML };
        drawer.classList.add('sm-recs-ready');
        forceRelayout(live);
      })
      .catch(function (e) {
        el.__smFetching = false;
      });
  }

  function loadDrawerRecs(drawer) {
    var el = recsEl(drawer);
    if (!el) return;

    // data-product-id comes from cart.items.first at RENDER time, so markup
    // produced while the cart was empty carries a blank one. Rather than wait
    // for a re-render, ask the cart what the lead product is and fill it in.
    if (!el.dataset.productId) {
      if (!el.__smIdLookup) {
        el.__smIdLookup = true;
        fetch('/cart.js', { cache: 'no-store' })
          .then(function (r) {
            return r.json();
          })
          .then(function (cart) {
            el.__smIdLookup = false;
            if (!cart || !cart.items || !cart.items.length) return;
            el.dataset.productId = cart.items[0].product_id;
            loadDrawerRecs(drawer);
          })
          .catch(function () {
            el.__smIdLookup = false;
          });
      }
      return;
    }

    if (recsAreLoaded(el)) {
      // Keep the cache current so the next cart re-render restores instantly.
      recsCache = { productId: el.dataset.productId, html: el.innerHTML };
      // Flag on <cart-drawer>, which survives the section re-render, so CSS can
      // hold the section's height during the brief blank before the restore.
      drawer.classList.add('sm-recs-ready');
      return;
    }

    // cart.js just replaced the drawer markup with an empty placeholder. If we
    // already have this product's list, put it straight back — same markup the
    // browser was showing a moment ago, so no round-trip and no gap.
    if (recsCache.html && recsCache.productId === el.dataset.productId) {
      if (recsNode) {
        // Move the SAME node across; its images stay decoded so nothing
        // repaints. Re-parsing the cached HTML recreated every <img>.
        if (recsNode.parentElement !== el) el.appendChild(recsNode);
      } else {
        el.innerHTML = recsCache.html;
        recsNode = el.querySelector('.rec-track');
      }
      forceRelayout(el);
      return;
    }

    // Nothing cached (first open, or the lead item changed) — go get it. The
    // health interval re-runs this, so a failed attempt simply retries.
    fetchDrawerRecs(drawer, el);
  }

  /* ------------------------------------------------------------------
   * Carousel: one card in view, arrows + dots + swipe stay in sync.
   *
   * Generic so the recommendations and the offers strip can share it. The
   * track is the scroll container; each element child is a slide. Dots are
   * generated into `dotsEl`. Re-running it on the same track is safe — it
   * rebuilds the dots and re-reads the slide count, which is what we want
   * after the drawer (or the recs list) is re-rendered.
   * ------------------------------------------------------------------ */
  function initCarousel(track, dotsEl, prevBtn, nextBtn) {
    if (!track) return;
    var slides = track.children;
    var count = slides.length;

    // A drawer-wide MutationObserver drives re-init, and building the dots is
    // itself a mutation — bail when nothing changed so the two can't ping-pong.
    if (
      track.__smInitCount === count &&
      (!dotsEl || dotsEl.children.length === count)
    ) {
      return;
    }
    var firstInit = track.__smInitCount === undefined;
    track.__smInitCount = count;

    // Nothing to page through: hide the chrome and bail.
    if (count <= 1) {
      if (dotsEl) dotsEl.innerHTML = '';
      [prevBtn, nextBtn].forEach(function (b) {
        if (b) b.style.display = count ? '' : 'none';
      });
      if (count === 1) {
        if (prevBtn) prevBtn.disabled = true;
        if (nextBtn) nextBtn.disabled = true;
      }
      return;
    }
    [prevBtn, nextBtn].forEach(function (b) {
      if (b) b.style.display = '';
    });

    var index = 0;

    function step() {
      var first = track.children[0];
      if (!first) return 0;
      var styles = window.getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return first.offsetWidth + gap;
    }

    function sync() {
      track.__smIndex = index;
      if (prevBtn) prevBtn.disabled = index <= 0;
      if (nextBtn) nextBtn.disabled = index >= count - 1;
      if (!dotsEl) return;
      var dots = dotsEl.children;
      for (var i = 0; i < dots.length; i++) {
        dots[i].classList.toggle('on', i === index);
      }
    }

    function go(i) {
      index = Math.max(0, Math.min(count - 1, i));
      track.scrollTo({ left: index * step(), behavior: 'smooth' });
      sync();
    }

    // Reuse dots when they are already correct — the offers strip renders its
    // dots server-side, and tearing them down on every re-render collapsed the
    // row for a frame and shifted the panel. Only rebuild when the count is
    // genuinely wrong (e.g. the recs list, whose length isn't known until the
    // fetch resolves).
    if (dotsEl) {
      if (dotsEl.children.length !== count) {
        dotsEl.innerHTML = '';
        for (var i = 0; i < count; i++) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.setAttribute('aria-label', 'Go to slide ' + (i + 1) + ' of ' + count);
          dotsEl.appendChild(dot);
        }
      }
      // (Re)bind click handlers, idempotently, to whichever dots are present.
      Array.prototype.forEach.call(dotsEl.children, function (dot, i) {
        if (dot.__smBound) return;
        dot.__smBound = true;
        dot.addEventListener('click', function () {
          go(i);
        });
      });
    }

    // Bind arrows once per button, but resolve the track from the DOM at CLICK
    // time — never close over it. The arrows live in the drawer snippet while
    // the track lives inside <product-recommendations>, which is replaced when
    // the recs fetch resolves; a captured reference would leave the buttons
    // scrolling a detached element (they appeared dead).
    track.__smGo = go;
    [
      [prevBtn, -1],
      [nextBtn, 1],
    ].forEach(function (pair) {
      var btn = pair[0];
      var dir = pair[1];
      if (!btn || btn.__smBound) return;
      btn.__smBound = true;
      btn.addEventListener('click', function () {
        var scope = btn.closest('[data-sm-recs], [data-sm-offers]');
        var live = scope && scope.querySelector('[data-sm-rec-track], [data-sm-offers-track]');
        if (live && typeof live.__smGo === 'function') {
          live.__smGo((live.__smIndex || 0) + dir);
        }
      });
    });

    // Keep index in sync with manual swipes.
    if (!track.__smScrollBound) {
      track.__smScrollBound = true;
      track.addEventListener(
        'scroll',
        function () {
          var s = step();
          if (!s) return;
          var i = Math.round(track.scrollLeft / s);
          if (i !== track.__smIndex) {
            track.__smIndex = i;
            index = i;
            sync();
          }
        },
        { passive: true }
      );
    }

    // Only rewind on the FIRST init for this track element. Re-initialising
    // (which the drawer observer triggers) must not yank the carousel back to
    // slide 1 — that is what made the arrows look dead: go() scrolled, then a
    // re-init immediately reset scrollLeft to 0.
    if (firstInit) {
      track.__smIndex = 0;
      track.scrollLeft = 0;
      index = 0;
    } else {
      index = track.__smIndex || 0;
    }
    sync();
  }

  // Wire the recommendations carousel (list is injected async by global.js).
  function initRecsCarousel(drawer) {
    var wrap = drawer.querySelector('[data-sm-recs]');
    if (!wrap) return;
    initCarousel(
      wrap.querySelector('[data-sm-rec-track]'),
      wrap.querySelector('[data-sm-rec-dots]'),
      wrap.querySelector('[data-sm-rec-prev]'),
      wrap.querySelector('[data-sm-rec-next]')
    );
  }

  // Offers strip: rendered server-side from the PDP offer settings, so it's
  // present as soon as the drawer markup is. No-ops until that markup exists.
  function initOffersCarousel(drawer) {
    var wrap = drawer.querySelector('[data-sm-offers]');
    if (!wrap) return;
    var track = wrap.querySelector('[data-sm-offers-track]');

    // Marquee mode: CSS animates the duplicated set. Don't run the stepping
    // carousel over it — dots/scroll-snap would fight the animation. Instead
    // pace the loop off the measured set width so the strip travels at a
    // constant speed whether there are two offers or five.
    if (wrap.classList.contains('sm-offers--marquee')) {
      var set = track && track.querySelector('.sm-offers__set');
      if (set) {
        // Slow, ambient drift — fast enough to notice, slow enough to read a
        // code without chasing it.
        var pxPerSecond = 20;
        var seconds = Math.max(28, Math.round(set.offsetWidth / pxPerSecond));
        track.style.animationDuration = seconds + 's';
      }
      return;
    }

    initCarousel(track, wrap.querySelector('[data-sm-offers-dots]'), null, null);
  }

  // Visual confirmation on the recommendation "+ Add" buttons. The actual add
  // is handled by sm-card-product.js (data-sm-card-add); this is feedback only,
  // and the drawer re-render that follows replaces the button anyway.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('.rec-add[data-sm-card-add]');
    if (btn) btn.classList.add('added');
  });

  // Recs markup, and the whole drawer body, arrive asynchronously (Section
  // Rendering swap, then the recommendations fetch). Observing the DRAWER —
  // not the recommendations element — means the observer survives those
  // innerHTML swaps, so the carousels always get wired once their cards land.
  // initCarousel() no-ops when the slide count is unchanged, which stops this
  // from ping-ponging with its own dot-building.
  function watchDrawer(drawer) {
    if (drawer.__smObserved) return;
    drawer.__smObserved = true;
    // Debounced + re-entrancy guarded: the callbacks below mutate the DOM
    // (building dots), which would otherwise retrigger this observer in a tight
    // loop and freeze the renderer.
    var pending = false;
    var running = false;
    var dirty = false;

    function pass() {
      pending = false;
      running = true;
      dirty = false;
      try {
        // cart.js re-renders this section on every quantity change / removal,
        // wiping the recs list — re-kick so the section comes back.
        loadDrawerRecs(drawer);
        initRecsCarousel(drawer);
        initOffersCarousel(drawer);
        updateItemsFade(drawer);
      } finally {
        // release after the mutations we just made have been queued
        setTimeout(function () {
          running = false;
          // A mutation that arrived mid-pass must NOT be dropped: cart.js's
          // re-render often lands exactly here, and swallowing it was why the
          // recommendations sometimes never came back. Run once more for it.
          if (dirty) schedule();
        }, 0);
      }
    }

    function schedule() {
      if (pending) return;
      pending = true;
      // short: the recs restore is synchronous, so keep the gap invisible
      setTimeout(pass, 30);
    }

    new MutationObserver(function () {
      if (running || pending) {
        dirty = true; // remember it; handled on the trailing pass
        return;
      }
      schedule();
    }).observe(drawer, { childList: true, subtree: true });
  }

  // Safety net. The MutationObserver is the fast path, but under rapid cart
  // updates a re-render can still land in a gap and leave the list empty. This
  // re-checks a few times a second while the drawer is open; loadDrawerRecs()
  // returns immediately when the list is already populated, so it costs
  // essentially nothing and guarantees the section comes back.
  function watchRecsHealth(drawer) {
    if (drawer.__smRecsTimer) return;
    var idleTicks = 0;
    drawer.__smRecsTimer = setInterval(function () {
      if (!drawer.classList.contains('active')) {
        // Tolerate a few inactive ticks before giving up. The class is applied
        // by Dawn's open() and briefly re-written by the section re-render, so
        // a single early tick could otherwise kill this watchdog for the whole
        // session — which is exactly why the list only appeared after a reopen
        // (that re-armed it). Still stops ~2s after a genuine close.
        if (++idleTicks >= 5) {
          clearInterval(drawer.__smRecsTimer);
          drawer.__smRecsTimer = null;
        }
        return;
      }
      idleTicks = 0;

      // A stale `is-empty` hides the whole footer (Dawn's empty-state layout),
      // which takes the recommendations with it. refreshDrawer() clears the
      // class, but if that fetch is slow or fails — likelier on a phone — the
      // class lingers and the section stays invisible even though it loaded.
      if (drawer.classList.contains('is-empty')) {
        fetch('/cart.js', { cache: 'no-store' })
          .then(function (r) {
            return r.json();
          })
          .then(function (cart) {
            if (cart && cart.item_count > 0) {
              drawer.classList.remove('is-empty');
              loadDrawerRecs(drawer);
            }
          })
          .catch(function () {});
      }

      // Defensive: if the section is missing entirely (older cached markup),
      // re-render the drawer so there is something to populate.
      if (!drawer.querySelector('.sm-drawer-recs')) {
        var now = new Date().getTime();
        if (!drawer.__smRemarkupAt || now - drawer.__smRemarkupAt > 3000) {
          drawer.__smRemarkupAt = now;
          fetch('/cart.js', { cache: 'no-store' })
            .then(function (r) {
              return r.json();
            })
            .then(function (cart) {
              // Only re-render when the cart actually has something in it;
              // an empty cart legitimately has no recommendations section.
              if (!cart || !cart.item_count) return;
              return refreshDrawer(drawer).then(function () {
                loadDrawerRecs(drawer);
              });
            })
            .catch(function () {});
        }
        return;
      }

      loadDrawerRecs(drawer);

      // Cards present but the subtree still has no boxes: force a relayout.
      var sec = drawer.querySelector('.sm-drawer-recs');
      if (sec && sec.querySelector('.rec') && sec.offsetHeight === 0) {
        forceRelayout(sec.querySelector('product-recommendations') || sec);
      }
    }, 400);
  }

  function openDrawer(trigger) {
    var drawer = getDrawer();
    if (!drawer) return false;
    // Open immediately — no network wait, so there's no click-to-open delay.
    tryOpen(drawer, trigger);
    watchDrawer(drawer);
    watchRecsHealth(drawer);
    // Then refresh contents in the background (just-added item, cross-tab
    // changes) and load the recommendations, while the drawer is already open.
    refreshDrawer(drawer).then(function () {
      loadDrawerRecs(drawer);
      initRecsCarousel(drawer);
      initOffersCarousel(drawer);
      updateItemsFade(drawer);
    });

    // Staged retries independent of the observer and the health interval. On a
    // slow phone the theme's own add-to-cart handler can re-render the drawer
    // after ours and wipe the list; these catch that without waiting on any
    // single mechanism. loadDrawerRecs() no-ops once cards are present.
    [300, 900, 1800, 3000].forEach(function (ms) {
      setTimeout(function () {
        loadDrawerRecs(drawer);
        initRecsCarousel(drawer);
      }, ms);
    });
    return true;
  }

  // 1) Header cart icon.
  document.addEventListener('click', function (event) {
    // Let modified / non-primary clicks do their native thing (e.g. Cmd/Ctrl+click
    // to open /cart in a new tab), and don't fight another handler that acted.
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    var link = event.target.closest(SELECTOR);
    if (!link) return;
    // Only fall through to native /cart navigation if there's genuinely no
    // drawer in the DOM. If the element exists we ALWAYS open it (tryOpen has a
    // fallback), so the icon never intermittently jumps to the cart page.
    if (!getDrawer()) return;
    event.preventDefault();
    openDrawer(link);
  });

  // 1b) Offer codes: copy to clipboard. The offers strip is informational —
  // codes are applied at checkout, so there is no apply/remove plumbing here.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-sm-pdp-offer-copy]');
    if (!btn) return;
    var drawer = getDrawer();
    if (!drawer || !drawer.contains(btn)) return; // PDP has its own handler
    event.preventDefault();
    var code = btn.getAttribute('data-sm-pdp-offer-copy');
    if (navigator.clipboard && code) {
      navigator.clipboard.writeText(code).catch(function () {});
    }
    btn.classList.add('is-copied');
    setTimeout(function () {
      btn.classList.remove('is-copied');
    }, 1400);
  });

  // 1c) Total breakdown: tapping the total expands subtotal / shipping.
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-sm-total]');
    if (!toggle) return;
    event.preventDefault();
    var drawer = getDrawer();
    var breakdown = drawer && drawer.querySelector('[data-sm-breakdown]');
    if (!breakdown) return;
    var opening = breakdown.hasAttribute('hidden');
    if (opening) {
      breakdown.removeAttribute('hidden');
    } else {
      breakdown.setAttribute('hidden', '');
    }
    toggle.setAttribute('aria-expanded', String(opening));
    var chev = toggle.querySelector('.sm-total__chev');
    if (chev) chev.classList.toggle('up', opening);
  });

  /* ------------------------------------------------------------------
   * Items scroll cue. Dawn's drawer scrolls on <cart-drawer-items> or on
   * .drawer__contents depending on viewport/content, so find whichever is
   * actually scrollable rather than assuming.
   * ------------------------------------------------------------------ */
  function getScroller(drawer) {
    var candidates = [
      drawer.querySelector('cart-drawer-items'),
      drawer.querySelector('.drawer__contents'),
      drawer.querySelector('.drawer__inner'),
    ];
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (el && el.scrollHeight - el.clientHeight > 4) return el;
    }
    return null;
  }

  function updateItemsFade(drawer) {
    var fade = drawer.querySelector('[data-sm-items-fade]');
    if (!fade) return;
    var el = getScroller(drawer);
    if (!el) {
      fade.classList.remove('show');
      return;
    }
    var more = el.scrollHeight - el.scrollTop - el.clientHeight > 4;
    fade.classList.toggle('show', more);

    if (!el.__smFadeBound) {
      el.__smFadeBound = true;
      el.addEventListener(
        'scroll',
        function () {
          updateItemsFade(drawer);
        },
        { passive: true }
      );
    }
  }

  window.addEventListener('resize', function () {
    var drawer = getDrawer();
    if (drawer) updateItemsFade(drawer);
  });

  // 2) Any successful add-to-cart, from any flow.
  function patchFetchForAdd() {
    if (window.__smDrawerAddFetchPatched) return;
    var orig = window.fetch;
    if (typeof orig !== 'function') return;
    window.__smDrawerAddFetchPatched = true;
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var p = orig.apply(this, arguments);
      if (isAddUrl(url)) {
        p.then(function (res) { if (res && res.ok) openDrawer(); }).catch(function () {});
      }
      return p;
    };
  }

  function patchXhrForAdd() {
    if (window.__smDrawerAddXhrPatched) return;
    window.__smDrawerAddXhrPatched = true;
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__smAddUrl = url || '';
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (isAddUrl(this.__smAddUrl)) {
        this.addEventListener('loadend', function () {
          if (this.status >= 200 && this.status < 300) openDrawer();
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  patchFetchForAdd();
  patchXhrForAdd();
})();
